/**
 * Mandanten-Einstellungen, die keiner Queue und keiner Quelle gehoeren
 * (Spec 3.2 / 8.5 / 8.6 / 10):
 *
 *   - CSAT an/aus und die Auswertung je Queue
 *   - Slack-Incoming-Webhook fuer Benachrichtigungen
 *   - SkillLock-Anbindung (Wahl einer Verbindung aus dem Vault)
 *
 * **Fuer SkillLock werden hier keine Zugangsdaten mehr eingetippt**
 * (ADR 0017): Basis-URL, Service-Token, Webhook-Secret und Standard-Board
 * stehen in einer `skilllock`-Verbindung, die der Betreiber unter „Plattform →
 * Verbindungen" pflegt und fuer diesen Mandanten freigibt. Hier wird nur noch
 * gewaehlt — und die Wahl **ist** der An-Schalter.
 *
 * Die Slack-URL bleibt: sie ist kein Anbieter-Zugang, sondern eine Adresse
 * dieses Mandanten (Spec 8.5).
 */
import type { ConnectionRef, CsatSummary, SkilllockSettings, Tenant } from '@tickethub/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { Select } from '@/components/ui/select';
import { connectionsApi, csatApi, skilllockApi, tenantApi } from '@/lib/tickets';

export function IntegrationsPage() {
  const { t } = useTranslation();
  const { activeMembership, reload: reloadSession } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [skilllock, setSkilllock] = useState<SkilllockSettings | null>(null);
  const [summary, setSummary] = useState<CsatSummary | null>(null);
  const [slackUrl, setSlackUrl] = useState('');
  const [connections, setConnections] = useState<ConnectionRef[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [tenantResult, skilllockResult, summaryResult, connectionResult] = await Promise.all([
        tenantApi.get(tenantId),
        skilllockApi.getSettings(tenantId),
        csatApi.summary(tenantId),
        connectionsApi.list(tenantId, 'skilllock'),
      ]);
      setTenant(tenantResult);
      setSkilllock(skilllockResult);
      setSummary(summaryResult);
      setSlackUrl(tenantResult.settings.slack_webhook_url ?? '');
      setConnections(connectionResult.items);
      setConnectionId(skilllockResult.connection_id ?? '');
    } catch (error) {
      toastError(error, t);
    }
  }, [tenantId, t]);

  useEffect(() => {
    // `reload` setzt State erst *nach* dem Netzwerk-Await — es entsteht keine
    // kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void reload();
  }, [reload]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
      await reload();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">{t('integrations.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('integrations.intro')}</p>
      </div>

      {/* ── Benachrichtigungen des Mandanten (Spec 8.5) ─────────────────── */}
      <section className="space-y-3 rounded-lg border p-4">
        <h2 className="text-lg font-semibold">{t('integrations.slack_title')}</h2>
        <p className="text-sm text-muted-foreground">{t('integrations.slack_intro')}</p>

        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void run(() =>
              tenantApi.update(tenantId ?? '', {
                settings: { slack_webhook_url: slackUrl.trim() === '' ? null : slackUrl.trim() },
              }),
            );
          }}
        >
          <div className="min-w-72 flex-1 space-y-1">
            <Label htmlFor="slack-url">{t('integrations.slack_url')}</Label>
            <Input
              id="slack-url"
              type="url"
              value={slackUrl}
              placeholder="https://hooks.slack.com/services/…"
              onChange={(event) => setSlackUrl(event.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy}>
            {t('common.save')}
          </Button>
        </form>
      </section>

      {/* ── CSAT (Spec 8.6) ──────────────────────────────────────────────── */}
      <section className="space-y-3 rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold">{t('csat.settings_title')}</h2>
          <Badge variant={tenant?.settings.csat_enabled ? 'default' : 'outline'}>
            {tenant?.settings.csat_enabled ? t('common.on') : t('common.off')}
          </Badge>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="ml-auto"
            disabled={busy || tenant === null}
            onClick={() =>
              void run(() =>
                tenantApi.update(tenantId ?? '', {
                  settings: { csat_enabled: !(tenant?.settings.csat_enabled ?? false) },
                }),
              )
            }
          >
            {tenant?.settings.csat_enabled ? t('common.turn_off') : t('common.turn_on')}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">{t('csat.settings_intro')}</p>

        {summary === null ? null : summary.requested === 0 ? (
          <p className="text-sm text-muted-foreground">{t('csat.summary_empty')}</p>
        ) : (
          <>
            <p className="text-sm">
              {t('csat.summary_overall', {
                average: summary.average?.toFixed(1) ?? '–',
                answered: summary.answered,
                requested: summary.requested,
              })}
            </p>
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ticket.queue')}</th>
                    <th className="px-3 py-2 font-medium">{t('csat.average')}</th>
                    <th className="px-3 py-2 font-medium">{t('csat.answered')}</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.queues.map((queue) => (
                    <tr key={queue.queue_id} className="border-t">
                      <td className="px-3 py-2">{queue.queue_name}</td>
                      <td className="px-3 py-2">{queue.average?.toFixed(1) ?? '–'}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {queue.answered} / {queue.requested}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {summary.recent.length > 0 ? (
              <ul className="space-y-2">
                {summary.recent.map((entry) => (
                  <li key={entry.id} className="text-sm">
                    <span className="font-mono text-xs text-muted-foreground">
                      {entry.ticket_number}
                    </span>{' '}
                    <Badge variant="outline">{entry.score}</Badge>{' '}
                    {entry.comment ? <span>„{entry.comment}"</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </section>

      {/* ── SkillLock (Spec 10) ──────────────────────────────────────────── */}
      <section className="space-y-3 rounded-lg border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold">{t('skilllock.settings_title')}</h2>
          <Badge variant={skilllock?.enabled ? 'default' : 'outline'}>
            {skilllock?.enabled ? t('common.on') : t('common.off')}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{t('skilllock.settings_intro')}</p>

        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await skilllockApi.saveSettings(tenantId ?? '', {
                connection_id: connectionId === '' ? null : connectionId,
              });
              await reloadSession();
            });
          }}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="sl-connection">{t('skilllock.connection')}</Label>
              <Select
                id="sl-connection"
                value={connectionId}
                onChange={(event) => setConnectionId(event.target.value)}
              >
                <option value="">{t('skilllock.connection_none')}</option>
                {connections.map((connection) => (
                  <option key={connection.id} value={connection.id}>
                    {connection.name}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">{t('skilllock.connection_hint')}</p>
              {connections.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('skilllock.no_connections')}</p>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="sl-base">{t('skilllock.base_url')}</Label>
              <Input id="sl-base" value={skilllock?.base_url ?? ''} readOnly disabled />
              <p className="text-xs text-muted-foreground">{t('skilllock.readonly_hint')}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="sl-board">{t('skilllock.default_board')}</Label>
              <Input id="sl-board" value={skilllock?.default_board_id ?? ''} readOnly disabled />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={busy}>
              {t('common.save')}
            </Button>
            {skilllock?.enabled ? (
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setConnectionId('');
                  void run(() => skilllockApi.saveSettings(tenantId ?? '', { connection_id: null }));
                }}
              >
                {t('common.turn_off')}
              </Button>
            ) : null}
          </div>
        </form>
      </section>
    </div>
  );
}
